import { useEffect, useRef, useState } from "react";
import { Alert, Form, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setAddressAndCost } from "../modules/delivery/actions";
import { Button } from "../styles";
import { client } from "../utils/api";

let googleMapsPromise;

function loadGoogleMaps() {
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google);
  }
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const callbackName = "__eatallyGoogleMapsReady";
    window[callbackName] = () => {
      delete window[callbackName];
      resolve(window.google);
    };
    const script = document.createElement("script");
    const params = new URLSearchParams({
      key: process.env.REACT_APP_GOOGLE_API_KEY,
      libraries: "places",
      loading: "async",
      callback: callbackName,
      v: "weekly",
      language: "pl",
      region: "PL",
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.onerror = () => {
      googleMapsPromise = null;
      delete window[callbackName];
      reject(new Error("Google Maps failed to load"));
    };
    document.head.appendChild(script);
  });
  return googleMapsPromise;
}

function errorMessage(error) {
  return (
    error?.json?.detail ||
    "Nie udało się sprawdzić adresu. Spróbuj ponownie za chwilę."
  );
}

function keepAutocompleteVisible(autocomplete) {
  if (!window.matchMedia("(max-width: 768px)").matches) return;

  window.requestAnimationFrame(() => {
    autocomplete.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  });
}

function SearchLocationForm() {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let disposed = false;
    let autocomplete;
    let listener;
    let focusListener;
    let viewportResizeListener;
    const container = containerRef.current;

    loadGoogleMaps()
      .then(async (google) => {
        const { PlaceAutocompleteElement } = await google.maps.importLibrary(
          "places"
        );
        if (disposed || !container) return;
        autocomplete = new PlaceAutocompleteElement({
          includedRegionCodes: ["pl"],
          requestedLanguage: "pl",
          requestedRegion: "pl",
        });
        autocomplete.classList.add("delivery-place-autocomplete");
        autocomplete.placeholder = "Wyszukaj swój adres dostawy";
        focusListener = () => keepAutocompleteVisible(autocomplete);
        viewportResizeListener = () => {
          if (autocomplete.matches(":focus-within")) {
            keepAutocompleteVisible(autocomplete);
          }
        };
        listener = async (event) => {
          setError("");
          const place = event.placePrediction.toPlace();
          try {
            await place.fetchFields({
              fields: ["id", "formattedAddress", "location"],
            });
            if (!place.id || !place.formattedAddress || !place.location) {
              throw new Error("Incomplete place");
            }
            setLocation({
              address: place.formattedAddress,
              place_id: place.id,
              latitude: place.location.lat(),
              longitude: place.location.lng(),
            });
          } catch (_) {
            setLocation(null);
            setError("Wybierz pełny adres z listy podpowiedzi.");
          }
        };
        autocomplete.addEventListener("focusin", focusListener);
        autocomplete.addEventListener("gmp-select", listener);
        window.visualViewport?.addEventListener(
          "resize",
          viewportResizeListener
        );
        container.replaceChildren(autocomplete);
      })
      .catch(() => setError("Nie udało się załadować wyszukiwarki adresów."));

    return () => {
      disposed = true;
      if (autocomplete && listener) {
        autocomplete.removeEventListener("gmp-select", listener);
      }
      if (autocomplete && focusListener) {
        autocomplete.removeEventListener("focusin", focusListener);
      }
      if (viewportResizeListener) {
        window.visualViewport?.removeEventListener(
          "resize",
          viewportResizeListener
        );
      }
      if (container) container.replaceChildren();
    };
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!location) {
      setError("Wybierz pełny adres z listy podpowiedzi.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await client("store/distance", {
        method: "POST",
        body: location,
      });
      dispatch(setAddressAndCost({ ...location, ...response }));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit} className="delivery-search-form">
      <div ref={containerRef} className="mb-2" />
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="delivery-search-actions">
        <Button
          type="submit"
          active
          disabled={loading || !location}
          className="delivery-search-submit"
          aria-busy={loading}
        >
          {loading && (
            <Spinner
              animation="border"
              variant="light"
              size="sm"
              aria-hidden="true"
            />
          )}
          <span>{loading ? "Sprawdzam…" : "Sprawdź adres"}</span>
          {!loading && <span aria-hidden="true">→</span>}
        </Button>
      </div>
    </Form>
  );
}

export default SearchLocationForm;
